<template>
  <div class="jwt-auth-form text-center">
    <div class="mb-4">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>
    <p class="text-muted">{{ $t('auth.jwt.authenticating') }}</p>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { AuthProviders } from '../../constants/authProviders'

/**
 * JWT Auto-Login Component
 *
 * Automatically authenticates user when JWT token is present in URL query params.
 * Usage: <JwtAuthForm @success="handleSuccess" @error="handleError" />
 *
 * This component is shown when ?jwt=xxx is detected in URL.
 */
export default {
  name: 'JwtAuthForm',

  computed: {
    ...mapGetters('auth', ['hasAcceptedRequiredPolicies', 'authProviderFactory']),

    /**
     * Get JWT token from URL query params
     */
    jwtToken() {
      return this.$route.query.jwt
    },

    /**
     * Get picacode from query params if present
     */
    picacode() {
      return this.$route.query.picacode
    },

    /**
     * Get redirect route name from query params with proper mapping
     */
    redirectRoute() {
      const redirectParam = this.$route.query.redirect || this.$route.query.next

      // Map backend redirect values to Vue Router route names
      const redirectMap = {
        'checkins': 'checkins-list',
        'checkins-list': 'checkins-list',
        'homepage': 'homepage'
      }

      return redirectMap[redirectParam] || 'checkins-list'
    },

    /**
     * Determine final redirect destination based on picacode presence
     * If picacode exists, go directly to checkins-detail
     * Otherwise use redirectRoute
     */
    finalRedirectOptions() {
      if (this.picacode) {
        // With picacode: go to checkins detail page
        return {
          name: 'checkins-detail',
          params: { picacode: this.picacode }
        }
      } else {
        // Without picacode: use redirect parameter
        return { name: this.redirectRoute }
      }
    }
  },

  mounted() {
    // Start auto-login as soon as component is mounted
    this.performJwtLogin()
  },

  methods: {
    ...mapActions('auth', ['authenticate']),

    /**
     * Perform JWT authentication and redirect
     */
    async performJwtLogin() {
      if (!this.jwtToken) {
        this.$emit('error', new Error('JWT token not found in URL'))
        return
      }

      try {
        // Get JWT provider from factory
        const provider = this.authProviderFactory.getProvider(AuthProviders.JWT)

        if (!provider) {
          throw new Error('JWT provider not available')
        }

        // Authenticate with JWT token using universal authenticate action
        await this.authenticate({
          method: AuthProviders.JWT,
          credentials: { token: this.jwtToken },
          remember: true
        })

        // Handle picacode if present (save to sessionStorage for later use)
        if (this.picacode) {
          sessionStorage.setItem('picacode', this.picacode)
        }

        // Check privacy policies
        if (!this.hasAcceptedRequiredPolicies) {
          // Redirect to terms page with next destination
          await this.$router.push({
            name: 'terms',
            params: { next: this.redirectRoute }
          })
        } else {
          // Redirect to intended destination (checkins-detail if picacode, otherwise redirectRoute)
          await this.$router.push(this.finalRedirectOptions)
        }

        // Emit success event
        this.$emit('success')

      } catch (error) {
        console.error('JWT authentication failed:', error)
        this.$emit('error', error)
      }
    }
  }
}
</script>

<style scoped>
.jwt-auth-form {
  padding: 2rem;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>
